/**
 * MA-ARCH-181: Lead Attribution Integrity Card
 * 
 * Shows attribution state for a single lead on the Lead Detail page.
 * Fields: Source, Confidence, Landing Page, Funnel, Profit Path, Notes.
 * 
 * Source and Confidence are always visually separate (Requirement #1).
 * Null fields are omitted cleanly or shown as "Not available".
 * Name enrichment failures do not block rendering (Requirement #4).
 */

import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Globe, Layers, Filter, Route, FileText } from "lucide-react";
import { ConfidenceBadge, SourceLabel } from "./ConfidenceBadge";
import { api } from "@/lib/api";

function InfoRow({ icon: Icon, label, value, testId }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2.5" data-testid={testId}>
      <Icon className="mt-0.5 h-3.5 w-3.5 text-slate-500 flex-shrink-0" />
      <div className="min-w-0">
        <div className="text-[11px] text-slate-500 uppercase tracking-wide">{label}</div>
        <div className="text-sm text-slate-200 break-words">{value}</div>
      </div>
    </div>
  );
}

export default function LeadAttributionIntegrityCard({ lead }) {
  const [names, setNames] = useState({});

  // Best-effort name enrichment for linked objects
  useEffect(() => {
    if (!lead) return;
    const resolved = {};
    const fetches = [];

    if (lead.profit_path_id) {
      fetches.push(
        api.get(`/profit-paths/${lead.profit_path_id}`)
          .then(r => { resolved.profitPath = r.data?.name; })
          .catch(() => {})
      );
    }
    if (lead.landing_page_version_id) {
      fetches.push(
        api.get(`/landing-pages/versions/${lead.landing_page_version_id}`)
          .then(r => { resolved.landingPage = r.data?.route_path ? `/${r.data.route_path}` : `Version ${r.data?.version_number || "—"}`; })
          .catch(() => {})
      );
    }
    if (lead.funnel_version_id) {
      fetches.push(
        api.get(`/funnel-versions/${lead.funnel_version_id}`)
          .then(r => { resolved.funnelVersion = r.data?.version_label || `Version ${r.data?.version_number || "—"}`; })
          .catch(() => {})
      );
    } else if (lead.funnel_id) {
      fetches.push(
        api.get(`/funnels/${lead.funnel_id}`)
          .then(r => { resolved.funnelVersion = r.data?.name; })
          .catch(() => {})
      );
    }

    if (fetches.length > 0) {
      Promise.allSettled(fetches).then(() => setNames({ ...resolved }));
    }
  }, [lead?.profit_path_id, lead?.landing_page_version_id, lead?.funnel_version_id, lead?.funnel_id]);

  if (!lead) return null;

  const hasAnyAttribution = lead.attribution_type || lead.attribution_confidence || lead.lead_source;
  if (!hasAnyAttribution) return null;

  const source = lead.lead_source;
  const confidence = lead.attribution_confidence;
  const notes = lead.attribution_notes;

  return (
    <Card className="glass" data-testid="lead-attribution-integrity-card">
      <CardContent className="p-0">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="text-lg font-semibold text-slate-100">Attribution</div>
          <ConfidenceBadge confidence={confidence} />
        </div>

        <div className="border-t border-white/10 px-4 py-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Source — always separate from Confidence */}
            <div data-testid="attribution-source-field">
              <div className="text-[11px] text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
                <Globe className="h-3 w-3" />
                Source
              </div>
              <div className="mt-0.5">
                <SourceLabel source={source} />
              </div>
            </div>

            {/* Attribution Type */}
            {lead.attribution_type && (
              <div data-testid="attribution-type-field">
                <div className="text-[11px] text-slate-500 uppercase tracking-wide">Type</div>
                <Badge className="mt-0.5 bg-indigo-500/15 text-indigo-200 ring-1 ring-indigo-400/20 font-normal text-xs">
                  {lead.attribution_type.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                </Badge>
              </div>
            )}

            {/* Linked objects — only when known */}
            <InfoRow
              icon={Route}
              label="Profit Path"
              value={names.profitPath}
              testId="attribution-profit-path"
            />
            <InfoRow
              icon={Layers}
              label="Landing Page"
              value={names.landingPage}
              testId="attribution-landing-page"
            />
            <InfoRow
              icon={Filter}
              label="Funnel"
              value={names.funnelVersion}
              testId="attribution-funnel"
            />
          </div>

          {/* Notes — only when helpful */}
          {notes && (
            <div className="mt-4 pt-3 border-t border-white/5" data-testid="attribution-notes">
              <div className="flex items-start gap-2">
                <FileText className="h-3.5 w-3.5 text-slate-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-slate-500 leading-relaxed">{notes}</p>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
